import { useState } from "react";

import { useLocalization } from "@fluent/react";
import { entities } from "byzantine";
import { parseErrorResponse } from "byzantine/src/dbbl/composites/request/request.helpers";
import {
  AccountNumberTextInput,
  AutocompleteStreetAddress,
  StateSelector,
  ZipCodeTextInput,
} from "cerulean";
import { Alert, ResponsiveFlex, Row, TextInput } from "@narmi/design_system";

import * as formAdapters from "../../../../../adapters";
import RoutingNumberInput from "../../../../../../components/RecipientDrawer/RoutingNumberInput";
import { useUserFeatures } from "../../../../../../components/contexts/UserFeaturesContext";
import SectionActions from "../../../common/SectionActions";
import SectionError from "../../../common/SectionError";

import type Address from "byzantine/src/Address";

interface WireFormProps {
  recipientId?: API.RecipientId;
  // the flow needs a domestic wire and this recipient doesn't have it yet
  showMissingDetailsAlert: boolean;
  // input the user hasn't saved is blocking another section
  showUnsavedError: boolean;
  onSaved: () => void;
  onCancel: () => void;
}

// saves the recipient's domestic wire details with their own API call
const WireForm = ({
  recipientId,
  showMissingDetailsAlert,
  showUnsavedError,
  onSaved,
  onCancel,
}: WireFormProps) => {
  const { l10n } = useLocalization();
  const { disallow_wire_address_pobox: disallowAddressPoBox } =
    useUserFeatures();
  const { form, handleSubmitCreate, handleSubmitUpdate, loading } =
    entities.recipients.useWireDestinationForm(
      recipientId,
      disallowAddressPoBox
    );
  const recipient = entities.recipients.useRecipient(recipientId);
  const [errorMessage, setErrorMessage] = useState<string>();
  const isAutocompleteApiAvailable = Boolean(
    (window as { google?: { maps?: unknown } }).google?.maps
  );
  const { onChange: onStreetAddressChange, ...streetAddressProps } =
    formAdapters.fieldWithOnChange(form, "address.streetAddress");

  // the hook passes on either its parsed message or the request's error response
  const handleSaveError = async (error: unknown) => {
    if (typeof error === "string") {
      setErrorMessage(error);
      return;
    }
    const { errors } = await parseErrorResponse(
      error as Parameters<typeof parseErrorResponse>[0]
    );
    setErrorMessage(errors[0]?.description || l10n.getString("error-generic"));
  };

  const handleClickSave = () => {
    setErrorMessage(undefined);
    const submit = recipient?.fedwire_destination
      ? handleSubmitUpdate
      : handleSubmitCreate;
    submit(() => {
      form.resetForm({ values: form.values });
      onSaved();
    }, handleSaveError);
  };

  const handleClickCancel = () => {
    form.resetForm();
    onCancel();
  };

  const handleAddressUpdate = ({
    street_address: streetAddress,
    city,
    region_code: regionCode,
    postal_code: postalCode,
  }: Address) => {
    form.setFieldValue("address.streetAddress", streetAddress);
    form.setFieldValue("address.city", city);
    form.setFieldValue("address.regionCode", regionCode);
    form.setFieldValue("address.postalCode", postalCode);
  };

  return (
    <ResponsiveFlex gapSize="s">
      {showMissingDetailsAlert ? (
        <Alert kind="warn" isActive icon="alert-triangle" isDismissable={false}>
          <div className="fontSize--s">
            {l10n.getString("add-fedwire-details-error-banner")}
          </div>
        </Alert>
      ) : null}
      <SectionError
        errorMessage={errorMessage}
        showUnsavedError={showUnsavedError}
      />
      <AccountNumberTextInput
        label={l10n.getString("labelAccountNumber")}
        {...formAdapters.fieldWithOnChange(form, "accountNumber")}
      />
      <RoutingNumberInput
        label={l10n.getString("label-routing")}
        routingType="fedwire"
        {...formAdapters.fieldWithOnChange(form, "routingNumber")}
      />
      <Row gapSize="xs">
        <Row.Item>
          {isAutocompleteApiAvailable ? (
            <AutocompleteStreetAddress
              streetAddressLabel={l10n.getString("label-street-address")}
              onChange={onStreetAddressChange}
              onUpdate={handleAddressUpdate}
              {...streetAddressProps}
            />
          ) : (
            <TextInput
              label={l10n.getString("label-street-address")}
              {...formAdapters.input(form, "address.streetAddress")}
            />
          )}
        </Row.Item>
        <Row.Item>
          <TextInput
            label={l10n.getString("label-address-2")}
            {...formAdapters.input(form, "address.streetAddress2")}
          />
        </Row.Item>
      </Row>
      <TextInput
        label={l10n.getString("label-city")}
        {...formAdapters.input(form, "address.city")}
      />
      <Row gapSize="xs">
        <Row.Item>
          <StateSelector
            label={l10n.getString("label-state")}
            {...formAdapters.fieldWithOnChange(form, "address.regionCode")}
          />
        </Row.Item>
        <Row.Item>
          <ZipCodeTextInput
            label={l10n.getString("label-postal")}
            {...formAdapters.fieldWithOnChange(form, "address.postalCode")}
          />
        </Row.Item>
      </Row>
      <SectionActions
        testIdSuffix="wire-destination"
        isSaving={loading}
        onCancel={handleClickCancel}
        onSave={handleClickSave}
      />
    </ResponsiveFlex>
  );
};

export default WireForm;
